import { useState } from "react"
import { Play, Pause, FastForward, SkipBack, ChevronDown } from "lucide-react"
import { cn } from "@/lib/utils"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../Popover/Popover"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../Tooltip/Tooltip"
import type { Candle } from "../types/chart"

export const DEFAULT_SPEEDS: readonly number[] = [0.5, 1, 2, 5, 10, 25, 50, 100]

export interface ChartReplayBarProps {
  ohlc: Candle[]
  playing: boolean
  speed: number
  visibleCount: number
  totalCandles: number
  /**
   * Upper bound for the scrub input. Used when only part of the underlying
   * data is buffered (e.g. a backtest still producing candles) so the user
   * cannot drag the thumb past the available region. Defaults to totalCandles.
   */
  maxScrubIndex?: number
  bufferPct?: number
  backtestRunning?: boolean
  onRestart: () => void
  onTogglePlay: () => void
  onCycleSpeed: () => void
  onSpeedChange: (speed: number) => void
  onScrub: (index: number) => void
  speeds?: readonly number[]
  className?: string
}

function formatSpeed(s: number) {
  return `${s}x`
}

function timeToDate(t: unknown): Date | null {
  if (t == null) return null
  if (typeof t === "number") return new Date(t * 1e3)
  if (typeof t === "string") return new Date(t)
  if (typeof t === "object" && t !== null && "year" in t) {
    const parts = t as { year: number; month: number; day: number }
    return new Date(Date.UTC(parts.year, parts.month - 1, parts.day))
  }
  return null
}

const DATE_FMT = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
})

function formatScrubDate(t: unknown): string {
  const d = timeToDate(t)
  if (!d || isNaN(d.getTime())) return ""
  const parts = DATE_FMT.formatToParts(d)
  const month = parts.find((p) => p.type === "month")?.value ?? ""
  const day = parts.find((p) => p.type === "day")?.value ?? ""
  const year = parts.find((p) => p.type === "year")?.value ?? ""
  const hour = parts.find((p) => p.type === "hour")?.value ?? ""
  const minute = parts.find((p) => p.type === "minute")?.value ?? ""
  return `${month} ${day} \u2019${year} \xB7 ${hour}:${minute}`
}

export function ChartReplayBar({
  ohlc,
  playing,
  speed,
  visibleCount,
  totalCandles,
  maxScrubIndex,
  bufferPct = 100,
  backtestRunning = false,
  onRestart,
  onTogglePlay,
  onCycleSpeed,
  onSpeedChange,
  onScrub,
  speeds = DEFAULT_SPEEDS,
  className,
}: ChartReplayBarProps) {
  const pct = totalCandles > 0 ? (visibleCount / totalCandles) * 100 : 0
  const currentIdx = Math.max(0, Math.min(visibleCount - 1, ohlc.length - 1))
  const scrubDate = formatScrubDate(ohlc[currentIdx]?.time)
  const effectiveMaxScrub = Math.max(1, Math.min(totalCandles, maxScrubIndex ?? totalCandles))
  // Hover state carries both the cursor x within the bar (for positioning)
  // and the bar width (for deriving the fraction → ohlc index → datetime),
  // so we don't have to re-measure the DOM on render.
  const [hover, setHover] = useState<{ x: number; width: number } | null>(null)
  const [speedOpen, setSpeedOpen] = useState(false)

  const handleTrackMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect()
    setHover({ x: e.clientX - rect.left, width: rect.width })
  }

  let hoverDate = ""
  if (hover !== null) {
    const fraction = hover.width > 0
      ? Math.max(0, Math.min(1, hover.x / hover.width))
      : 0
    const hoverIdx = Math.min(ohlc.length - 1, Math.floor(fraction * ohlc.length))
    hoverDate = formatScrubDate(ohlc[hoverIdx]?.time)
  }

  return (
    <TooltipProvider delayDuration={300}>
      <div
        className={cn(
          "px-4 pt-2 pb-4 flex items-center gap-0 sm:gap-2 shrink-0 border-t border-b border-border bg-background",
          className,
        )}
      >
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={onRestart}
              disabled={backtestRunning}
              className={cn(
                "w-9 h-9 2xl:w-7 2xl:h-7 rounded-md flex items-center justify-center transition-colors shrink-0",
                backtestRunning
                  ? "text-muted-foreground/40 cursor-not-allowed"
                  : "text-muted-foreground hover:bg-muted hover:text-foreground cursor-pointer",
              )}
            >
              <SkipBack size={14} />
            </button>
          </TooltipTrigger>
          <TooltipContent>
            {backtestRunning ? "Backtest running…" : "Restart replay"}
          </TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={onTogglePlay}
              disabled={backtestRunning}
              className={cn(
                "w-9 h-9 2xl:w-7 2xl:h-7 rounded-md flex items-center justify-center transition-colors shrink-0",
                backtestRunning
                  ? "text-muted-foreground/40 cursor-not-allowed"
                  : playing
                    ? "bg-primary text-primary-foreground hover:bg-primary/90 cursor-pointer"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground cursor-pointer",
              )}
            >
              {playing ? (
                <Pause size={14} fill="currentColor" strokeWidth={0} />
              ) : (
                <Play size={14} fill="currentColor" strokeWidth={0} />
              )}
            </button>
          </TooltipTrigger>
          <TooltipContent>
            {backtestRunning ? "Backtest running…" : playing ? "Pause replay" : "Play replay"}
          </TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={onCycleSpeed}
              disabled={backtestRunning}
              className={cn(
                "w-9 h-9 2xl:w-7 2xl:h-7 rounded-md flex items-center justify-center transition-colors shrink-0",
                backtestRunning
                  ? "text-muted-foreground/40 cursor-not-allowed"
                  : "text-muted-foreground hover:bg-muted hover:text-foreground cursor-pointer",
              )}
            >
              <FastForward size={14} />
            </button>
          </TooltipTrigger>
          <TooltipContent>
            {backtestRunning ? "Backtest running…" : `Cycle speed (${speed}x)`}
          </TooltipContent>
        </Tooltip>

        <Popover open={speedOpen} onOpenChange={setSpeedOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className={cn(
                "inline-flex items-center gap-0.5 shrink-0 h-9 2xl:h-7 px-1.5 rounded-md cursor-pointer transition-colors",
                "text-caption tabular-nums hover:bg-muted",
                speed > 1 || speed < 1 ? "text-foreground font-medium" : "text-muted-foreground",
              )}
              title="Playback speed"
            >
              {formatSpeed(speed)}
              <ChevronDown
                size={10}
                strokeWidth={2}
                className={cn("opacity-50 transition-transform", speedOpen && "rotate-180")}
              />
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="v1 w-20 p-1">
            {speeds.map((s) => {
              const active = s === speed
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => {
                    onSpeedChange(s)
                    setSpeedOpen(false)
                  }}
                  className={cn(
                    "w-full text-left px-2 py-1 rounded text-caption tabular-nums cursor-pointer transition-colors",
                    active
                      ? "bg-muted text-foreground font-medium"
                      : "text-muted-foreground hover:bg-muted/50 hover:text-foreground",
                  )}
                >
                  {formatSpeed(s)}
                </button>
              )
            })}
          </PopoverContent>
        </Popover>

        <div
          className="flex-1 relative h-1.5 2xl:h-[3px] rounded-sm bg-[rgba(0,0,0,0.09)] dark:bg-[rgba(255,255,255,0.09)]"
          onMouseEnter={handleTrackMove}
          onMouseMove={handleTrackMove}
          onMouseLeave={() => setHover(null)}
        >
          <div
            className="absolute top-0 left-0 h-full rounded-sm pointer-events-none bg-[rgba(0,0,0,0.22)] dark:bg-[rgba(255,255,255,0.22)] transition-[width] duration-700 ease-out"
            style={{ width: `${bufferPct}%` }}
          />
          <div
            className="absolute top-0 left-0 h-full rounded-sm pointer-events-none bg-primary"
            style={{ width: `${pct}%` }}
          />
          <input
            type="range"
            min={1}
            max={effectiveMaxScrub}
            value={Math.min(visibleCount, effectiveMaxScrub)}
            onChange={(e) => onScrub(Number(e.target.value))}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          />
          <div
            className="absolute top-1/2 -translate-y-1/2 pointer-events-none rounded-full shadow-sm w-3.5 h-3.5 2xl:w-2.5 2xl:h-2.5 bg-[#1a1a1a] dark:bg-white border-2 border-primary"
            style={{ left: `calc(${pct}% - 7px)` }}
          />
          {scrubDate && (
            <div
              className="absolute top-full mt-1 pointer-events-none text-caption text-muted-foreground whitespace-nowrap"
              style={{
                left: `${pct}%`,
                transform: pct < 3 ? "translateX(0)" : pct > 97 ? "translateX(-100%)" : "translateX(-50%)",
              }}
            >
              {scrubDate}
            </div>
          )}
          {hover !== null && hoverDate && (
            <div
              className="absolute bottom-full mb-2 pointer-events-none text-caption text-foreground whitespace-nowrap -translate-x-1/2 bg-popover border border-border rounded-md px-2 py-1 shadow-popover z-10"
              style={{ left: `${hover.x}px` }}
            >
              {hoverDate}
            </div>
          )}
        </div>

        <span className="text-caption text-muted-foreground whitespace-nowrap shrink-0 tabular-nums ml-4">
          {Math.round(pct)}%
        </span>
      </div>
    </TooltipProvider>
  )
}
